import type { Subscription } from "@/context/SubscriptionContext";
import { backendFetch } from "@/lib/backend";
import { sanitizeText } from "@/lib/sanitize";

type PrivacyExport = {
  exportedAt: string;
  subscriptions: Array<Record<string, unknown>>;
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const csvEscape = (value: unknown) => {
  const text = value == null ? "" : sanitizeText(value, 2_000);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const subscriptionsToCsv = (subscriptions: Subscription[] | Array<Record<string, unknown>>) => {
  const headers = ["Name", "Plan", "Price", "Cycle", "Next bill", "Status", "Category", "Payment label"];
  const rows = subscriptions.map((sub) => [
    sub.name,
    sub.plan,
    sub.price,
    sub.cycle,
    "nextBill" in sub ? sub.nextBill : sub.next_bill,
    sub.status,
    sub.category,
    sub.card,
  ]);

  return [headers, ...rows].map((row) => row.map(csvEscape).join(",")).join("\n");
};

const buildPdf = (title: string, lines: string[]) => {
  const objects: string[] = [];
  const addObject = (body: string) => {
    objects.push(body);
    return objects.length;
  };

  const escapedLines = [title, ...lines].map((line) =>
    String(line).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)")
  );
  const content = [
    "BT",
    "/F1 18 Tf",
    "72 760 Td",
    `(${escapedLines[0]}) Tj`,
    "/F1 10 Tf",
    "0 -28 Td",
    ...escapedLines.slice(1, 38).flatMap((line) => [`(${line}) Tj`, "0 -16 Td"]),
    "ET",
  ].join("\n");

  const catalogId = addObject("<< /Type /Catalog /Pages 2 0 R >>");
  addObject("<< /Type /Pages /Kids [3 0 R] /Count 1 >>");
  addObject("<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>");
  addObject("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  addObject(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((body, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return pdf;
};

export const downloadSubscriptionsCsv = async () => {
  const data = await backendFetch("/privacy/export", { forceRefresh: true }) as { export?: PrivacyExport };
  const exported = data.export;
  const csv = subscriptionsToCsv(exported?.subscriptions || []);
  downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `orizonte-subscriptions-${new Date().toISOString().slice(0, 10)}.csv`);
};

export const downloadSubscriptionsPdf = async () => {
  const data = await backendFetch("/privacy/export", { forceRefresh: true }) as { export?: PrivacyExport };
  const exported = data.export;
  const subscriptions = exported?.subscriptions || [];
  const totalMonthly = subscriptions.reduce((sum, sub) => {
    const price = Number(sub.price || 0);
    return sum + (sub.cycle === "Yearly" ? price / 12 : price);
  }, 0);
  const lines = [
    `Exported: ${exported?.exportedAt ? new Date(exported.exportedAt).toLocaleString() : new Date().toLocaleString()}`,
    `Subscriptions: ${subscriptions.length}`,
    `Monthly run rate: $${totalMonthly.toFixed(2)}`,
    "",
    ...subscriptions.map((sub) => `${sanitizeText(sub.name || "Subscription", 120)} - $${Number(sub.price || 0).toFixed(2)} ${sanitizeText(sub.cycle || "", 40)} - ${sanitizeText(sub.next_bill || "", 40)}`),
  ];
  const pdf = buildPdf("Orizonte Subscription Report", lines);
  downloadBlob(new Blob([pdf], { type: "application/pdf" }), `orizonte-report-${new Date().toISOString().slice(0, 10)}.pdf`);
};
